import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, Legend,
} from "recharts";
import { ArrowLeft } from "lucide-react";

function formatKg(v) {
    if (v == null || isNaN(v)) return "—";
    return `${Number(v).toLocaleString("en-ZA", { maximumFractionDigits: 1 })} kg`;
}

const PERIODS = [
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
    { label: "365d", days: 365 },
    { label: "All", days: 3650 },
];

export default function ProductDetail() {
    const { sku } = useParams();
    const [days, setDays] = useState(365);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/products/${encodeURIComponent(sku)}?days=${days}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [sku, days]);

    if (loading && !data) return <div className="text-[color:var(--bl-text-dim)]">Loading…</div>;
    if (!data) return null;

    const p = data.product;
    const s = data.summary;
    // build stock series merged across regions
    const allTs = new Set();
    Object.values(data.stock_series || {}).forEach(arr => arr.forEach(x => allTs.add(x.ts)));
    const stockMerged = [...allTs].sort().map((ts) => {
        const row = { ts: ts.slice(0, 10) };
        for (const [region, arr] of Object.entries(data.stock_series || {})) {
            const point = arr.find((x) => x.ts === ts);
            row[region] = point ? point.qty : null;
        }
        return row;
    });

    return (
        <div>
            <Link to="/products" className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-blue)] mb-3" data-testid="back-to-products">
                <ArrowLeft className="w-3 h-3" /> Back to Products
            </Link>

            <div className="flex items-start gap-5 mb-6">
                {p.image_url && (
                    <img src={p.image_url} alt="" className="w-28 h-28 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />
                )}
                <div className="flex-1">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                            <h1 className="font-serif-display text-4xl tracking-tight">{p.title}</h1>
                            <div className="font-mono-num text-xs text-[color:var(--bl-text-dim)] mt-1">SKU {p.sku} · status {p.status}</div>
                        </div>
                        <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)]">
                            {PERIODS.map((per) => (
                                <button
                                    key={per.days}
                                    data-testid={`product-period-${per.label}`}
                                    onClick={() => setDays(per.days)}
                                    className={`px-3 py-1.5 text-xs uppercase tracking-wider ${
                                        days === per.days
                                            ? "bg-[color:var(--bl-takealot-blue)] text-white"
                                            : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"
                                    }`}
                                >{per.label}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-5 mt-3 text-sm text-[color:var(--bl-text-dim)]">
                        <div>Price <span className="font-mono-num text-[color:var(--bl-text)]">{formatZAR(p.selling_price)}</span></div>
                        <div>RRP <span className="font-mono-num">{formatZAR(p.rrp)}</span></div>
                        <div>Kg/unit <span className="font-mono-num text-[color:var(--bl-text)]">{p.kg_per_unit || "—"}</span></div>
                        <div>COGS <span className="font-mono-num text-[color:var(--bl-text)]">{formatZAR(p.cost)}</span></div>
                        <div>Listing quality <span className="font-mono-num">{p.listing_quality ?? "—"}</span></div>
                        <div>Conv 30d <span className="font-mono-num">{p.conversion_percentage_30_days != null ? `${Number(p.conversion_percentage_30_days).toFixed(1)}%` : "—"}</span></div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-6">
                <KpiCard label="Revenue" value={formatZAR(s.revenue)} testid="pd-kpi-revenue" />
                <KpiCard label="Units" value={formatInt(s.units)} accent="orange" sub={`${formatInt(s.orders)} orders`} testid="pd-kpi-units" />
                <KpiCard label="Kg" value={formatKg(s.kg)} accent="coffee" testid="pd-kpi-kg" />
                <KpiCard label="Fees" value={formatZAR(s.fees)} accent="orange" testid="pd-kpi-fees" />
                <KpiCard label="Net profit" value={formatZAR(s.net)} accent="green" sub={s.margin_pct != null ? `${s.margin_pct.toFixed(1)}% margin` : ""} testid="pd-kpi-net" />
                <KpiCard label="Returns" value={formatInt(s.returns)} accent="orange" sub={`${s.return_rate_pct.toFixed(1)}% rate`} testid="pd-kpi-returns" />
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm mb-6" data-testid="product-sales-chart">
                <div className="font-serif-display text-xl mb-3">Sales history</div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.daily}>
                            <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                            <XAxis dataKey="date" stroke="#75614f" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                            <YAxis yAxisId="l" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                            <YAxis yAxisId="r" orientation="right" stroke="#6b4423" fontSize={10} />
                            <Tooltip contentStyle={{ background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line yAxisId="l" dataKey="revenue" name="Revenue" stroke="#0079c2" strokeWidth={2} dot={false} />
                            <Line yAxisId="l" dataKey="net" name="Net" stroke="#e35a00" strokeWidth={2} dot={false} />
                            <Line yAxisId="r" dataKey="units" name="Units" stroke="#6b4423" strokeWidth={1.5} dot={false} />
                            <Line yAxisId="r" dataKey="returns" name="Returns" stroke="#c0392b" strokeWidth={1.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="product-stock-chart">
                    <div className="font-serif-display text-xl mb-3">Stock history per warehouse</div>
                    {stockMerged.length === 0 ? (
                        <div className="text-sm text-[color:var(--bl-text-dim)] py-10 text-center">
                            No stock snapshots yet. Each sync records the current stock per region — the chart will populate as you sync over time.
                        </div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stockMerged}>
                                    <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                    <XAxis dataKey="ts" stroke="#75614f" fontSize={10} />
                                    <YAxis stroke="#75614f" fontSize={10} />
                                    <Tooltip contentStyle={{ background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {Object.keys(data.stock_series || {}).map((region, i) => (
                                        <Area key={region} dataKey={region} stackId="1" stroke={["#0079c2", "#e35a00", "#6b4423", "#2f7a4d"][i % 4]} fill={["#0079c2", "#e35a00", "#6b4423", "#2f7a4d"][i % 4]} fillOpacity={0.25} />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        {(p.warehouses || []).map((w) => (
                            <div key={w.region} className="border border-[color:var(--bl-border)] rounded-sm p-2 bg-[color:var(--bl-bg)]" data-testid={`pd-warehouse-${w.region}`}>
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">{w.region}</div>
                                <div className="font-mono-num text-lg">{formatInt(w.quantity_available)}</div>
                                <div className="text-[10px] text-[color:var(--bl-text-dim)]">cover {w.stock_30_days_cover ?? "—"}d · sold30 {w.quantity_sold_30_days || 0}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="product-exceptions-table">
                    <div className="font-serif-display text-xl mb-3">Exceptions & returns</div>
                    {data.exceptions.length === 0 ? (
                        <div className="text-sm text-[color:var(--bl-text-dim)] py-6 text-center">No returns for this product. Nice.</div>
                    ) : (
                        <div className="overflow-x-auto max-h-80 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] sticky top-0 bg-[color:var(--bl-surface)]">
                                    <tr>
                                        <th className="text-left py-2">Date</th>
                                        <th className="text-left py-2">RRN</th>
                                        <th className="text-right py-2">Qty</th>
                                        <th className="text-left py-2">Reason</th>
                                        <th className="text-left py-2">Region</th>
                                        <th className="text-left py-2">Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.exceptions.map((e, i) => (
                                        <tr key={i} className="border-b border-[color:var(--bl-border)]">
                                            <td className="py-2 font-mono-num text-xs">{e.date}</td>
                                            <td className="py-2 font-mono-num text-xs">{e.rrn}</td>
                                            <td className="py-2 text-right font-mono-num">{e.qty}</td>
                                            <td className="py-2 text-xs">{(e.reason || "").replace(/_/g, " ")}</td>
                                            <td className="py-2 text-xs">{e.region}</td>
                                            <td className="py-2 text-xs">{(e.outcome || "").replace(/_/g, " ")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
